import { useCallback, useEffect, useRef } from "react";
import type { AuthFlowState, AuthStep } from "./useAuthFlow";

export type NavMode = "push" | "replace" | "transient" | "stay";

/**
 * Classify a step transition for browser-history mirroring (ZC-5958).
 *
 * push      — the previous screen is a legitimate Back target. Includes
 *             chain-search (email -> chain-search: support SSO lands here
 *             from entry 0) and device-consent (company-chooser ->
 *             device-consent: unanswered consent IS back-able).
 * replace   — the new screen supersedes the current entry: terminals
 *             (device-approved/denied — company-chooser -> device-consent
 *             -> device-approved: an answered RFC 8628 consent must not be
 *             resurrectable; its user_code is one-shot), token screens
 *             (one-shot ?invite/?reset), and reset-sent (the submitted
 *             forgot form isn't worth revisiting). Also the fallback for
 *             transitions to email (company-chooser -> email: safety net;
 *             real resets use restart() instead).
 * transient — spinners; never recorded, so Back can never land on "loading".
 * stay      — same step (loading flags, error messages): refresh the
 *             current entry's snapshot in place.
 *
 * The switch is exhaustive over the remaining AuthStep values (sso-loading /
 * invite-loading are filtered above as transient); adding a new AuthStep
 * without extending this switch is a compile error via the `never` check in
 * default, so new steps are always a deliberate policy decision.
 */
export function navModeFor(recorded: AuthStep, next: AuthStep): NavMode {
  if (next === "sso-loading" || next === "invite-loading") return "transient";
  if (recorded === next) return "stay";
  switch (next) {
    case "verification":
    case "method-selection":
    case "password":
    case "portal-chooser":
    case "company-chooser":
    case "chain-search":
    case "forgot-password":
    case "device-consent":
      return "push";
    case "email":
    case "set-password":
    case "reset-sent":
    case "token-error":
    case "device-approved":
    case "device-denied":
      return "replace";
    default: {
      const exhausted: never = next;
      return exhausted;
    }
  }
}

const HISTORY_KEY = "zenAuthIdx";

/**
 * Mirrors login-step transitions into browser history (ZC-5958), so Back and
 * Forward work within a live SPA session without the URL ever encoding auth
 * state.
 *
 * Design contract:
 * - `history.state` carries `{ zenAuthIdx: number }` ONLY. Full AuthFlowState
 *   snapshots (email, identityId, company lists, etc.) live in `entriesRef`
 *   and die with the page — identity data must never enter `history.state`,
 *   because browsers persist it (session history, "Frequently visited",
 *   restore-tabs) well past the life of this login attempt.
 * - One owner: nothing OUTSIDE this hook module ever touches `window.history`
 *   — every pushState/replaceState/go call in the codebase lives here. Within
 *   the module, several places call replaceState (the mount effect's initial
 *   seed, the pending-reset branch of the popstate handler, restart()'s
 *   index-0 branch) but the recording effect below is the only one that ever
 *   PUSHes — it's the sole place a new entry is created. In-app Back
 *   delegates to `history.back()` (via the returned `back()`), which fires
 *   `popstate` and lets the same effect's sibling handler restore the
 *   snapshot — there is no separate "go back in app state" code path.
 * - StrictMode idempotency: this app mounts under React.StrictMode
 *   (login/index.tsx), which double-invokes effects in dev. The mount effect
 *   re-seeds `entriesRef`/`indexRef` from scratch each time it runs (safe —
 *   it's driven by the current `state`, not accumulated), and the recording
 *   effect only pushes on a genuine `navModeFor(...) === "push"` transition;
 *   a re-render with the same committed step is classified "stay" and never
 *   double-pushes.
 * - Staleness guard: any popstate whose `event.state` doesn't carry a known
 *   `zenAuthIdx` (foreign entry, or null from a pre-mount/pre-SPA history
 *   entry), or a `pageshow` with `persisted: true` (bfcache restore — e.g.
 *   Back from the IdP, or Back into this tab after login navigated away),
 *   triggers `window.location.reload()`. A fresh mount re-derives legitimate
 *   state from the URL + cookies rather than trusting a snapshot that may be
 *   stale or an index that may not exist. `markCompleted()` is called by the
 *   consumer right before `window.location.href` exits the SPA for a
 *   terminal redirect; any popstate observed after that point (e.g. the
 *   browser back button pressed during/after the redirect) also reloads,
 *   since the in-memory snapshot stack is no longer trustworthy once the
 *   page is on its way out.
 */
export function useLoginHistory(
  state: AuthFlowState,
  setState: React.Dispatch<React.SetStateAction<AuthFlowState>>
) {
  const entriesRef = useRef<AuthFlowState[]>([]);
  const indexRef = useRef(0);
  const pendingResetRef = useRef<AuthFlowState | null>(null);
  const completedRef = useRef(false);
  // Defensive, not load-bearing today: the mount effect below only runs once
  // in practice (LoginApp mounts exactly once per page life — a single
  // createRoot in login/index.tsx). stateRef exists so that IF the mount
  // effect ever re-ran (e.g. a future real remount, not StrictMode's
  // mount+unmount+remount-with-same-instance cycle), it seeds entriesRef
  // from the CURRENT state at that time rather than the stale state captured
  // in the effect's closure at first mount.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    entriesRef.current = [stateRef.current];
    indexRef.current = 0;
    window.history.replaceState({ [HISTORY_KEY]: 0 }, "");

    const onPopState = (event: PopStateEvent) => {
      if (completedRef.current) {
        window.location.reload();
        return;
      }
      const pending = pendingResetRef.current;
      if (pending) {
        pendingResetRef.current = null;
        entriesRef.current = [pending];
        indexRef.current = 0;
        window.history.replaceState({ [HISTORY_KEY]: 0 }, "");
        setState(pending);
        return;
      }
      // This guard's soundness relies on LoginApp mounting exactly once per
      // page life (a single createRoot call in login/index.tsx): entriesRef
      // is seeded once and only grows via this hook's own pushState calls, so
      // any zenAuthIdx outside its bounds is genuinely foreign (a different
      // page's history entry) rather than a stale-but-still-valid-shaped
      // index from an earlier instance of this hook. If the SPA ever
      // remounted mid-session, a stale zenAuthIdx that happens to still fall
      // within the NEW (shorter) entriesRef bounds would be wrongly treated
      // as reachable — the reload guard below only catches indices that are
      // out of bounds or missing, not ones that are in-bounds but semantically
      // stale.
      const idx = (event.state as Record<string, unknown> | null)?.[
        HISTORY_KEY
      ];
      if (typeof idx === "number" && entriesRef.current[idx] !== undefined) {
        indexRef.current = idx;
        setState(entriesRef.current[idx]);
        return;
      }
      window.location.reload(); // foreign/lost entry: only a fresh mount is trustworthy
    };

    const onPageShow = (event: Event) => {
      if ((event as { persisted?: boolean }).persisted) {
        window.location.reload();
      }
    };

    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [setState]);

  useEffect(() => {
    const entries = entriesRef.current;
    if (entries.length === 0) return;
    const recorded = entries[indexRef.current];
    const mode = navModeFor(recorded.step, state.step);
    if (mode === "transient") return;
    if (mode === "push") {
      entries.splice(indexRef.current + 1);
      entries.push(state);
      indexRef.current = entries.length - 1;
      window.history.pushState({ [HISTORY_KEY]: indexRef.current }, "");
      return;
    }
    entries[indexRef.current] = state;
    if (mode === "replace") {
      window.history.replaceState({ [HISTORY_KEY]: indexRef.current }, "");
    }
  }, [state]);

  const back = useCallback(() => {
    // A restart() traversal is already in flight (history.go(-N) queued but
    // its popstate hasn't landed yet) — indexRef is stale until it does.
    // Queuing another traversal here would race it, so no-op instead.
    // Scope: this guard only covers the in-app back() path. A PHYSICAL Back
    // press in the same window bypasses it; the pending-reset branch in
    // onPopState still lands the correct reset state, and the leftover second
    // popstate falls through to the out-of-bounds guard, costing one spurious
    // reload of an already-correct page. Accepted: restart()'s only callers
    // are logout and back-to-sign-in, so the window is a same-tick race on a
    // rare deliberate action.
    if (pendingResetRef.current) return;
    if (indexRef.current > 0) window.history.back();
  }, []);

  const restart = useCallback(
    (next: AuthFlowState) => {
      if (indexRef.current === 0) {
        entriesRef.current = [next];
        window.history.replaceState({ [HISTORY_KEY]: 0 }, "");
        setState(next);
        return;
      }
      pendingResetRef.current = next;
      window.history.go(-indexRef.current);
    },
    [setState]
  );

  const markCompleted = useCallback(() => {
    completedRef.current = true;
  }, []);

  return { back, restart, markCompleted };
}
